import React, { useState, useEffect } from 'react';
import { PivotSignalDataPoint } from '../../../api/metrics';
import { getSignalMeta } from '../utils/signalConfig';
import { TimezoneMode, formatModalTimestamp } from '../../../utils/timezone';
import './SignalDetailModal.css';


// Simple chevron icons as SVG components
const ChevronLeft: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

interface SignalDetailModalProps {
  signals: PivotSignalDataPoint[];
  onClose: () => void;
  timezone: TimezoneMode;
}

const SignalDetailModal: React.FC<SignalDetailModalProps> = ({ signals, onClose, timezone }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset currentIndex when signals array changes or length changes (e.g., switching timeframes)
  useEffect(() => {
    if (currentIndex >= signals.length) {
      setCurrentIndex(0);
    }
  }, [signals, signals.length, currentIndex]);

  if (!signals || signals.length === 0) return null;

  // Safety check: ensure currentIndex is within bounds
  const safeIndex = Math.min(currentIndex, signals.length - 1);
  const currentSignal = signals[safeIndex];

  // If we don't have a valid signal, don't render
  if (!currentSignal) return null;


  const getStrengthLabel = (strength: number) => {
    if (strength >= 0.8) return 'Very Strong';
    if (strength >= 0.6) return 'Strong';
    if (strength >= 0.4) return 'Moderate';
    if (strength >= 0.2) return 'Weak';
    return 'Very Weak';
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? signals.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === signals.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  const { date, time } = formatModalTimestamp(currentSignal.timestamp, timezone);
  const signalMeta = getSignalMeta(currentSignal.type);

  // Format detected at timestamp
  const detectedAt = formatModalTimestamp(currentSignal.createdAt, timezone);

  if (!signalMeta) {
    return null; // Unknown signal type
  }

  const Icon = signalMeta.Icon;

  return (
    <div className="signal-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="signal-modal-content" onClick={(e) => e.stopPropagation()} tabIndex={0}>
        <div className="signal-modal-header">
          <div className="signal-header-info">
            <span className="signal-timestamp">{date} at {time}</span>
            {signals.length > 1 && (
              <span className="signal-count-badge">
                Signal {safeIndex + 1} of {signals.length}
              </span>
            )}
          </div>
          <button className="signal-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="signal-modal-body">
          <div className="signal-card">
            <div className="signal-type-badge" data-type={currentSignal.type.toLowerCase()}>
              <Icon size={18} className="signal-icon" />
              <span className="signal-type-text">{signalMeta.label}</span>
            </div>

            <div className="signal-detail-row">
              <span className="detail-label">Level:</span>
              <span className="detail-value level-value">{currentSignal.level}</span>
            </div>

            <div className="signal-detail-row">
              <span className="detail-label">Price:</span>
              <span className="detail-value price-value">
                ${currentSignal.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
              </span>
            </div>

            <div className="signal-detail-row">
              <span className="detail-label">Strength:</span>
              <div className="strength-display">
                <div className="strength-bar-container">
                  <div
                    className="strength-bar-fill"
                    style={{ width: `${currentSignal.strength * 100}%` }}
                    data-strength={getStrengthLabel(currentSignal.strength).toLowerCase().replace(' ', '-')}
                  />
                </div>
                <span className="strength-label">
                  {getStrengthLabel(currentSignal.strength)} ({(currentSignal.strength * 100).toFixed(0)}%)
                </span>
              </div>
            </div>

            <div className="signal-detail-row">
              <span className="detail-label">Timeframe:</span>
              <span className="detail-value">{currentSignal.timeframe}</span>
            </div>

            {detectedAt && (
              <div className="signal-detail-row">
                <span className="detail-label">Created:</span>
                <span className="detail-value detected-at-value">
                  {detectedAt.time}
                </span>
              </div>
            )}

            <div className="signal-description">
              <p>{signalMeta.description}</p>
            </div>
          </div>
        </div>

        {signals.length > 1 && (
          <div className="signal-navigation">
            <button
              className="nav-button nav-previous"
              onClick={handlePrevious}
              aria-label="Previous signal"
            >
              <ChevronLeft size={20} />
              <span>Previous</span>
            </button>

            <div className="signal-dots">
              {signals.map((_, index) => (
                <button
                  key={index}
                  className={`signal-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to signal ${index + 1}`}
                />
              ))}
            </div>

            <button
              className="nav-button nav-next"
              onClick={handleNext}
              aria-label="Next signal"
            >
              <span>Next</span>
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalDetailModal;

